import React from 'react';

/**
 * Marca de cada buzón de correo (2026-07-27; sin marcas escritas, 2026-09-28).
 *
 * Todo sale de la cuenta que manda el servidor (`GET /api/mail/cuentas`):
 * `label`, `color` e `iniciales` los declara cada clínica en su `.env`
 * (MAIL_*_LABEL / _COLOR / _INICIALES). Antes aquí vivían los logotipos y
 * los colores de dos marcas concretas, así que cualquier copia del producto
 * enseñaba en su bandeja la marca de otra.
 *
 * - Sin color declarado, un gris neutro: nunca el color de nadie.
 * - Sin iniciales declaradas, las del propio nombre del buzón.
 * - Un logo solo si la cuenta trae uno (`logo`, una URL); si no, el cuadrito
 *   con las iniciales sobre el color de la cuenta.
 *
 * Los estilos derivados (fondos suaves, bordes, anillos) se calculan del
 * color con estilos en línea: una tabla de clases de Tailwind no sirve para
 * colores que solo se conocen al cargar la página.
 */

/** El tono del buzón que no ha declarado el suyo. Neutro a propósito. */
export const COLOR_NEUTRO = '#64748B';

/** `#RGB` o `#RRGGBB` → [r, g, b]; cualquier otra cosa → null. */
function rgb(hex) {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((hex || '').trim());
  if (!m) return null;
  const h = m[1].length === 3 ? m[1].split('').map((c) => c + c).join('') : m[1];
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
}

/** El color de la cuenta, validado: un valor raro del `.env` no rompe nada. */
export function colorBuzon(cuenta) {
  return rgb(cuenta?.color) ? cuenta.color.trim() : COLOR_NEUTRO;
}

const conAlfa = (hex, alfa) => `rgba(${rgb(hex).join(', ')}, ${alfa})`;

/** ¿Va mejor texto oscuro encima de este color? (luminancia relativa, WCAG) */
function esClaro(hex) {
  const [r, g, b] = rgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.45;
}

/** Iniciales del cuadro: las declaradas o, si no hay, las del nombre. */
export function inicialesBuzon(cuenta) {
  const declaradas = (cuenta?.iniciales || '').trim();
  if (declaradas) return declaradas;
  const palabras = (cuenta?.label || '').trim().split(/\s+/).filter(Boolean);
  return palabras.slice(0, 2).map((p) => p[0].toUpperCase()).join('') || '@';
}

/**
 * Los estilos de un buzón, listos para `style={…}`: el mismo color en el
 * punto, la pastilla, el botón activo, la barra lateral de cada fila y el
 * fondo de los no leídos, para que el color diga de qué buzón es cada cosa.
 */
export function estiloBuzon(cuenta) {
  const c = colorBuzon(cuenta);
  return {
    color: c,
    corto: cuenta?.label || '',
    punto: { backgroundColor: c },
    solido: { backgroundColor: c },
    textoSolido: esClaro(c) ? '#13231E' : '#FFFFFF',
    chip: { backgroundColor: conAlfa(c, 0.15), color: c, borderColor: conAlfa(c, 0.6) },
    activo: { borderColor: c, backgroundColor: conAlfa(c, 0.1), boxShadow: `0 0 0 2px ${c}` },
    barra: { borderLeft: `6px solid ${c}` },
    fondoNoLeido: { backgroundColor: conAlfa(c, 0.06) },
    tinte: { backgroundColor: conAlfa(c, 0.06) },
    anillo: { boxShadow: `0 0 0 2px ${c}` },
  };
}

/**
 * Cuadro de marca. `size` en píxeles (44 en el selector, 28 en filas).
 * Redondeado y recortado, para que el logo y el monograma ocupen el mismo
 * hueco exacto y las tarjetas queden alineadas.
 */
export default function MarcaBuzon({ cuenta, size = 36, className = '', ...resto }) {
  const estilo = { width: size, height: size };
  if (cuenta?.logo) {
    return (
      <img
        src={cuenta.logo}
        alt=""                 /* decorativo: el nombre del buzón va al lado */
        aria-hidden="true"
        style={estilo}
        className={`logo-flotante object-contain shrink-0 ${className}`}
        {...resto}
      />
    );
  }
  const e = estiloBuzon(cuenta);
  const iniciales = inicialesBuzon(cuenta);
  return (
    <span
      aria-hidden="true"
      style={{ ...estilo, backgroundColor: e.color, color: e.textoSolido }}
      className={`rounded-[10px] shrink-0 font-semibold inline-flex items-center
        justify-center tracking-tight ${className}`}
      data-color={e.color}
      {...resto}
    >
      <span style={{ fontSize: Math.round(size * (iniciales.length > 2 ? 0.3 : 0.38)) }}>
        {iniciales}
      </span>
    </span>
  );
}
